import { type ApiProblem, type ApiSchemas, call, newIdempotencyKey } from '@rental/contracts/client'
import {
  customerName,
  LOCALES,
  type Locale,
  STAFF_BOOKING_SOURCES,
  type StaffBookingSource,
} from '@rental/domain'
import { format, formatEuro, localized, MESSAGES, plural } from '@rental/i18n'
import { Button, Checkbox, cn, Field, Notice } from '@rental/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link, useBlocker, useNavigate } from '@tanstack/react-router'
import { type FormEvent, type Ref, useEffect, useId, useMemo, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { addDays } from '../board'
import { ActionRail, RAIL_FORM, RAIL_STEPS } from '../components/ActionRail'
import { ChoiceChips } from '../components/ChoiceChips'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Disclosure } from '../components/Disclosure'
import { LookupProblem } from '../components/LookupProblem'
import { PageHeader } from '../components/PageHeader'
import { Fetching } from '../components/Results'
import { Section } from '../components/Section'
import { Guard } from '../components/states'
import {
  FIELD,
  FIELD_ROW,
  FieldPair,
  Loading,
  PAIRED_FIELD,
  ProblemNotice,
  SHORT_FIELD,
  StaffInput,
  StaffSelect,
  StaffTextarea,
} from '../components/ui'
import { type Chosen, CustomerPicker } from '../customer/CustomerPicker'
import { noImplicitSubmit } from '../forms'
import { useLocale } from '../i18n'
import { parseEuroCents } from '../money'
import { e164 } from '../phone'
import { problemOf, problemText, type StaffProblem } from '../problems'
import { lineLabel } from '../quote-lines'
import { type Lookups, refusalText } from '../refusals'
import type { NewBookingSearch } from '../router'
import { romeInstant, romeNow, roundUp } from '../time'
import { isDirty } from './new-booking-dirty'
import {
  EMPTY_OLD_SITE,
  type OldSiteFields,
  type OldSiteReading,
  readOldSite,
} from './new-booking-old-site'
import { OldSiteSection, OldSiteSummary, useOldSiteGapWords, WaivedRules } from './OldSiteSection'

const route = getRouteApi('/bookings/new')

type Quote = ApiSchemas['StaffQuoteResponse']

interface CustomerFields {
  firstName: string
  lastName: string
  email: string
  phone: string
  birthDate: string
  nationality: string
}

interface LicenceFields {
  number: string
  country: string
  category: string
  firstIssued: string
  expires: string
  hasIdp: boolean
}

const EMPTY_CUSTOMER: CustomerFields = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  birthDate: '',
  nationality: '',
}
const EMPTY_LICENCE: LicenceFields = {
  number: '',
  country: 'IT',
  category: 'B',
  firstIssued: '',
  expires: '',
  hasIdp: false,
}

/** "location:<id>" or "zone:<id>", as the place selects hold them. */
function placeRef(value: string): { locationId: string } | { zoneId: string } | null {
  const [kind, id] = value.split(':')
  if (!id) return null
  return kind === 'zone' ? { zoneId: id } : { locationId: id }
}

/**
 * RES-1, J1 and J2: a booking for a walk-in, phone, WhatsApp or e-mail
 * customer. The price is the server's quote; the counter may replace it with
 * a reason (PRC-4) when its role allows. Payment links stay PENDING.
 *
 * S9 (ADM-4): or a future booking made on the old site, carried over by hand
 * (bookings.carry_over): it costs the price agreed there, the old site's own
 * rules come back as warnings, and what was paid there is shown back before
 * it is recorded. No promo code and no manual price: the agreed price is both.
 *
 * UI-2d: the steps on panes, and beside them, from 1024 px, the rail with the
 * price and the one button, which says what it waits for. It is pressed only
 * on the price of the rental on screen: never on the last one's while the
 * next is computed, nor on any for a rental left incomplete, where it used to
 * press and do nothing.
 */
export function NewBookingScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const search = route.useSearch()
  // Who may not book reads the refusal under the title, and nothing is asked of the server
  // for a form they could not send (UI-2g). Another customer's link starts a fresh form,
  // never a half-filled one. A Timeline drag arrives with the vehicle and range already chosen.
  const { customer, ...range } = search
  return (
    <Guard allowed={can(access, 'bookings.create')} title={m.newBooking.title}>
      <NewBookingForm key={JSON.stringify(search)} customerId={customer ?? null} prefill={range} />
    </Guard>
  )
}

type Prefill = Omit<NewBookingSearch, 'customer'>

function NewBookingForm({ customerId, prefill }: { customerId: string | null; prefill: Prefill }) {
  const { locale, messages: m } = useLocale()
  const { access, takeOver } = useAccess()
  const navigate = useNavigate()
  const [today] = useState(() => romeNow())

  const [source, setSource] = useState<StaffBookingSource | 'old_site'>('walk_in')
  const [oldSite, setOldSite] = useState<OldSiteFields>(EMPTY_OLD_SITE)
  const canCarryOver = can(access, 'bookings.carry_over')
  const carrying = source === 'old_site' && canCarryOver
  // Someone else unlocked, without the permission: the form becomes a booking of the counter's.
  useEffect(() => {
    if (source === 'old_site' && !canCarryOver) setSource('walk_in')
  }, [source, canCarryOver])
  const [pickupDate, setPickupDate] = useState(prefill.pickupDate ?? today.date)
  const [pickupTime, setPickupTime] = useState(prefill.pickupTime ?? roundUp(today.time))
  const [returnDate, setReturnDate] = useState(prefill.returnDate ?? addDays(today.date, 1))
  // The return at the rental day's boundary, the company's configuration (rental_day.boundary,
  // CLAUDE.md), read from the server and never written here (UI-2g). Until it is read, or if
  // it is not set, the field is empty and the rail asks for the time; what is typed wins. A
  // Timeline drag arrives with a return time already chosen, which stands as a typed one.
  const [typedReturnTime, setReturnTime] = useState<string | null>(prefill.returnTime ?? null)
  const [pickupPlace, setPickupPlace] = useState('')
  const [returnPlace, setReturnPlace] = useState('')
  const [categoryId, setCategoryId] = useState(prefill.category ?? '')
  const [vehicleId, setVehicleId] = useState(prefill.vehicle ?? '')
  const [extras, setExtras] = useState<Record<string, number>>({})
  const [coverageIds, setCoverageIds] = useState<string[]>([])
  const [promoInput, setPromoInput] = useState('')
  const [promoCode, setPromoCode] = useState('')
  const [customerMode, setCustomerMode] = useState<'new' | 'existing'>(
    customerId ? 'existing' : 'new',
  )
  const [chosen, setChosen] = useState<Chosen | null>(null)
  // Someone the counter chose themselves: the customer the booking was started for, if their
  // record did not come, is no longer their concern, and never replaces them if it comes
  // later (UI-2g).
  const [choseAnother, setChoseAnother] = useState(false)
  const [customer, setCustomer] = useState<CustomerFields>(EMPTY_CUSTOMER)
  const [licence, setLicence] = useState<LicenceFields>(EMPTY_LICENCE)
  const [customerLocale, setCustomerLocale] = useState<Locale>('it')
  const [override, setOverride] = useState(false)
  const [overrideTotal, setOverrideTotal] = useState('')
  const [overrideReason, setOverrideReason] = useState('')
  const [notes, setNotes] = useState('')
  // S9 (RES-6): the ferry a delivery to a port meets, its arrival on the pick-up's day.
  const [ferryName, setFerryName] = useState('')
  const [ferryTime, setFerryTime] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  // OPS-1: leaving on purpose (a booking made, Annulla confirmed) is never asked about.
  const leaving = useRef(false)
  const discardDialog = useRef<HTMLDialogElement>(null)
  const carryDialog = useRef<HTMLDialogElement>(null)
  // The rental whose price the server said had changed: for it, the price is read again.
  const [priceChangedFor, setPriceChangedFor] = useState<string | null>(null)
  const lastSent = useRef<{ body: string; key: string } | null>(null)
  const lastCarried = useRef<{ body: string; key: string } | null>(null)
  const pickupSelect = useRef<HTMLSelectElement>(null)
  const categorySelect = useRef<HTMLSelectElement>(null)
  const optionsArea = useRef<HTMLDivElement>(null)
  const refusalsId = useId()
  const noRoomId = useId()

  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: () => load(call(api.GET('/v1/locations'))),
    staleTime: Number.POSITIVE_INFINITY,
  })
  const zones = useQuery({
    queryKey: ['zones'],
    queryFn: () => load(call(api.GET('/v1/zones'))),
    staleTime: Number.POSITIVE_INFINITY,
  })
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => load(call(api.GET('/v1/categories'))),
    staleTime: Number.POSITIVE_INFINITY,
  })
  const site = useQuery({
    queryKey: ['site'],
    queryFn: () => load(call(api.GET('/v1/site'))),
    staleTime: Number.POSITIVE_INFINITY,
  })
  // S2: counting 24-hour periods, a return at the pick-up's own time ends a whole day; by the
  // boundary, the boundary does. With the method unset there is no default: the rail asks.
  const method = site.data?.rentalDayMethod
  const returnTime =
    typedReturnTime ??
    (method === 'periods24'
      ? pickupTime
      : method === 'boundary'
        ? site.data?.rentalDayBoundary
        : null) ??
    ''
  const options = useQuery({
    queryKey: ['category-options', categoryId],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/categories/{categoryId}/options', {
            params: { path: { categoryId } },
          }),
        ),
      ),
    enabled: categoryId !== '',
    staleTime: 60_000,
  })

  // The first terminal is the default pick-up place.
  const firstLocation = locations.data?.items[0]?.id
  useEffect(() => {
    if (!pickupPlace && firstLocation) setPickupPlace(`location:${firstLocation}`)
  }, [pickupPlace, firstLocation])

  // A customer chosen from their record (CUS-4: "New booking for this customer").
  // Applied once: "Change" must be able to clear it.
  const preset = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/customers/{customerId}', {
            params: { path: { customerId: customerId ?? '' } },
          }),
        ),
      ),
    enabled: customerId !== null,
  })
  const presetProblem = problemOf(preset.error)
  const presetMissing =
    presetProblem?.kind === 'api' && (presetProblem.status === 404 || presetProblem.status === 400)
  // S8: a record erased, or whose owner asked to be erased, takes no new booking: the
  // server would refuse it at the quote, so it is said before anything is typed.
  const presetRefused = preset.data
    ? preset.data.customer.anonymisedAt !== null
      ? m.errors.codes.customer_not_found
      : preset.data.customer.erasureRequestedOn !== null
        ? m.errors.codes.customer_erasure_requested
        : null
    : null
  const presetApplied = useRef(false)
  useEffect(() => {
    if (preset.data && !presetApplied.current) {
      presetApplied.current = true
      const found = preset.data.customer
      if (found.anonymisedAt !== null || found.erasureRequestedOn !== null) return
      setChosen({
        id: found.id,
        name: customerName(found),
        reachable: Boolean(found.email || found.phone),
      })
    }
  }, [preset.data])

  // OPS-1: what the counter can change, as it stands. The first render's values are the clean
  // form, a Planning drag's prefill included; the app's own defaults (the first terminal as the
  // pick-up place, the customer a link brought) are left out, so they never count as typed.
  const touched = {
    source,
    pickupDate,
    pickupTime,
    returnDate,
    typedReturnTime,
    pickupPlace: pickupPlace === `location:${firstLocation ?? ''}` ? '' : pickupPlace,
    returnPlace,
    categoryId,
    vehicleId,
    extras,
    coverageIds,
    promoInput,
    promoCode,
    customerMode,
    chosen: customerId === null || choseAnother ? (chosen?.id ?? null) : null,
    customer,
    licence,
    customerLocale,
    override,
    overrideTotal,
    overrideReason,
    notes,
    ferryName,
    ferryTime,
    oldSite,
  }
  const initial = useRef(touched)
  const dirty = isDirty(initial.current, touched)
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  // The menu, Back, a link: with typed values on the form, the same question as Annulla asks.
  // The browser's own unload prompt stays off: the idle lock drops the form anyway (C2), and
  // the prompt cannot be answered by the E2E.
  const blocker = useBlocker({
    shouldBlockFn: () => dirtyRef.current && !leaving.current,
    withResolver: true,
    enableBeforeUnload: false,
  })
  useEffect(() => {
    if (blocker.status === 'blocked') discardDialog.current?.showModal()
  }, [blocker.status])
  function leave() {
    leaving.current = true
    void navigate({ to: '/bookings' })
  }
  function cancel() {
    if (dirty) discardDialog.current?.showModal()
    else leave()
  }
  function discard() {
    discardDialog.current?.close()
    if (blocker.status === 'blocked') blocker.proceed()
    else leave()
  }

  const startsAt = romeInstant(pickupDate, pickupTime)
  const endsAt = romeInstant(returnDate, returnTime)
  const pickup = placeRef(pickupPlace)
  const back = placeRef(returnPlace || pickupPlace)
  // S9 (RES-6): a delivery to a port meets the customer's ferry; elsewhere there is none to ask.
  const atPort =
    pickup !== null &&
    'zoneId' in pickup &&
    zones.data?.items.find((zone) => zone.id === pickup.zoneId)?.kind === 'port'
  const ferryArrival = atPort && ferryTime ? romeInstant(pickupDate, ferryTime) : null
  const ferry =
    atPort && ferryName.trim() && ferryArrival
      ? { name: ferryName.trim(), arrivalAt: ferryArrival }
      : null
  const ferryPartial = atPort && (ferryName.trim() !== '' || ferryTime !== '') && ferry === null
  // What both the quote and the booking ask about: the same rental.
  const period = useMemo(() => {
    if (!categoryId || !startsAt || !endsAt || !pickup || !back) return null
    if (Date.parse(endsAt) <= Date.parse(startsAt)) return null
    return {
      categoryId,
      startsAt,
      endsAt,
      pickup,
      return: back,
      extras: Object.entries(extras)
        .filter(([, quantity]) => quantity > 0)
        .map(([extraId, quantity]) => ({ extraId, quantity })),
      coverageIds,
      // S9: the agreed price has whatever the old site gave: no code here.
      ...(promoCode && !carrying ? { promoCode } : {}),
    }
  }, [categoryId, startsAt, endsAt, pickup, back, extras, coverageIds, promoCode, carrying])
  // The driver's age changes the price (CUS-7): a chosen customer's, or the typed birth date.
  // S9: priced as a booking from the old site, the rules it was agreed without come back waived.
  const quoteBody = useMemo(() => {
    if (!period) return null
    const asked = carrying ? { ...period, oldSite: true as const } : period
    if (customerMode === 'existing' && chosen) return { ...asked, customerId: chosen.id }
    return customer.birthDate ? { ...asked, driverBirthDate: customer.birthDate } : asked
  }, [period, carrying, customerMode, chosen, customer.birthDate])
  const quoteKey = quoteBody ? JSON.stringify(quoteBody) : null
  const priceChanged = quoteKey !== null && priceChangedFor === quoteKey

  const quote = useQuery({
    queryKey: ['staff-quote', quoteBody],
    queryFn: () => {
      if (!quoteBody) throw new Error('quote asked without a rental')
      return load(call(api.POST('/v1/staff/quote', { body: quoteBody })))
    },
    enabled: quoteBody !== null,
    placeholderData: keepPreviousData,
  })
  // The price on screen: the last one while the next is on its way, and none for a rental
  // that is not complete.
  const shown = quoteBody ? (quote.data ?? null) : null
  // The price that may be booked: this rental's own, in, and not one the server has just said
  // has changed (it is being read again).
  const current =
    quoteBody && !quote.isPlaceholderData && !(priceChanged && quote.isFetching)
      ? (quote.data ?? null)
      : null
  const quoting = quoteBody !== null && current === null && !quote.isError
  // The server said the price had changed and it is being read again: the button the counter
  // pressed waits busy, with the focus, where waiting for the price would disable it (UI-2g).
  const rereading = priceChanged && quote.isFetching

  // A chosen vehicle that the new price says is taken is dropped.
  useEffect(() => {
    if (
      vehicleId &&
      current &&
      !current.vehicles.some((vehicle) => vehicle.id === vehicleId && vehicle.free)
    ) {
      setVehicleId('')
    }
  }, [vehicleId, current])

  const lookups: Lookups = {
    locations: locations.data?.items ?? [],
    zones: zones.data?.items ?? [],
    extras: options.data?.extras ?? [],
  }
  const remote = source !== 'walk_in'
  const canOverride = can(access, 'prices.override')
  // A booking from the old site costs the price agreed there: never a manual one besides.
  const manualPrice = override && canOverride && !carrying
  const carried = readOldSite(oldSite)
  const gapWords = useOldSiteGapWords()
  const overrideCents = manualPrice ? parseEuroCents(overrideTotal) : null
  // Until a rental is complete and priced, the vehicles have nothing to be free for.
  const vehiclesWaiting = quoteBody === null || quote.data === undefined

  function customerBody() {
    if (customerMode === 'existing') return chosen ? { id: chosen.id } : null
    if (!customer.firstName.trim() || !customer.lastName.trim()) return null
    return {
      firstName: customer.firstName.trim(),
      lastName: customer.lastName.trim(),
      ...(customer.email.trim() ? { email: customer.email.trim() } : {}),
      ...(customer.phone.trim() ? { phone: e164(customer.phone) } : {}),
      ...(customer.birthDate ? { birthDate: customer.birthDate } : {}),
      ...(customer.nationality.trim() ? { nationality: customer.nationality.trim() } : {}),
    }
  }

  // What the button waits for, each in words, in the page's order. The server keeps every
  // rule: these say what it would refuse before the counter asks (a remote booking with no
  // way back to the customer, contact_required).
  const reachable =
    customerMode === 'existing'
      ? (chosen?.reachable ?? true)
      : Boolean(customer.email.trim() || customer.phone.trim())
  // A discount code the price did not take: the booking would be refused for it (promo_refused).
  const promo = current?.quote?.promo
  const promoRefused =
    promo && !promo.applied
      ? format(m.newBooking.promoRefused, {
          code: promo.code,
          why: m.newBooking.promoReasons[promo.reason],
        })
      : null
  // S9: the old site's section comes first on the page, so do its gaps.
  const reasons: string[] = carrying ? carried.gaps.map((gap) => gapWords[gap]) : []
  if (!startsAt || !endsAt) reasons.push(m.newBooking.needWhen)
  else if (Date.parse(endsAt) <= Date.parse(startsAt)) reasons.push(m.refusals.invalid_period)
  if (!pickup) reasons.push(m.newBooking.needPlace)
  if (ferryPartial) reasons.push(m.newBooking.needFerry)
  if (!categoryId) reasons.push(m.newBooking.needCategory)
  if (promoRefused) reasons.push(promoRefused)
  if (quoting) reasons.push(m.newBooking.quoting)
  else if (quoteBody && !current?.quote && !current?.refusals.length)
    reasons.push(m.newBooking.noQuote)
  if (customerMode === 'existing' ? chosen === null : customerBody() === null) {
    reasons.push(customerMode === 'existing' ? m.newBooking.needCustomer : m.newBooking.needName)
  }
  if (remote && !reachable) {
    reasons.push(carrying ? m.newBooking.oldSite.contactNeeded : m.newBooking.contactNeeded)
  }
  // A manual price needs an amount the counter can read and a reason: an empty
  // total is not zero euros (it was, until day 8).
  if (manualPrice && overrideCents === null) reasons.push(m.newBooking.needOverrideTotal)
  if (manualPrice && overrideReason.trim().length < 3) {
    reasons.push(m.newBooking.needOverrideReason)
  }
  // What the price itself says stops it: its refusals, or no vehicle free.
  const refused = (current?.refusals.length ?? 0) > 0
  const noRoom = current !== null && !current.available && vehicleId === ''
  const stoppedBy = [...(refused ? [refusalsId] : []), ...(noRoom ? [noRoomId] : [])]
  const ready = reasons.length === 0 && stoppedBy.length === 0 && Boolean(current?.quote) && !busy

  /** The licence as both requests take it, when its number is typed. */
  function licenceBody() {
    if (!licence.number.trim()) return {}
    return {
      licence: {
        number: licence.number.trim(),
        issuingCountry: licence.country.trim(),
        categories: [
          {
            code: licence.category.trim() || 'B',
            ...(licence.firstIssued ? { firstIssuedOn: licence.firstIssued } : {}),
          },
        ],
        ...(licence.expires ? { expiresOn: licence.expires } : {}),
        hasIdp: licence.hasIdp,
      },
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    const who = customerBody()
    // Sent only from the rail's button, which waits until all of it is there.
    if (!ready || !period || !current?.quote || !who) return
    setProblem(null)
    setPriceChangedFor(null)
    // S9: what the old site agreed and took is shown back first (D11); the dialog records it.
    if (carrying) {
      carryDialog.current?.showModal()
      return
    }
    // Never sent as another channel: without the permission the source turns back by itself.
    if (source === 'old_site') return
    const body: ApiSchemas['StaffBookingRequestInput'] = {
      ...period,
      source,
      ...(vehicleId ? { vehicleId } : {}),
      customer: who,
      ...licenceBody(),
      expectedTotalCents: current.quote.totalCents,
      ...(overrideCents !== null
        ? {
            priceOverride: {
              totalCents: overrideCents,
              reason: overrideReason.trim(),
            },
          }
        : {}),
      locale: customerLocale,
      ...(ferry ? { ferry } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    }
    // One key per request: a retry of the same request replays, a changed one is new.
    const serialized = JSON.stringify(body)
    if (lastSent.current?.body !== serialized) {
      lastSent.current = { body: serialized, key: newIdempotencyKey() }
    }
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings', {
        body,
        params: { header: { 'idempotency-key': lastSent.current.key } },
      }),
    )
    setBusy(false)
    if (result.ok) {
      leaving.current = true
      void navigate({
        to: '/bookings/$code',
        params: { code: result.data.code },
        search: { created: true },
      })
      return
    }
    if (takeOver(result.problem)) return
    if (result.problem.kind === 'api' && result.problem.code === 'price_changed') {
      setPriceChangedFor(quoteKey)
      void quote.refetch()
      return
    }
    setProblem(result.problem)
  }

  /** S9: the booking from the old site, recorded once its amounts were shown back. */
  async function carryOver() {
    const who = customerBody()
    if (!carrying || !ready || !period || !who || !carried.request) return
    const body: ApiSchemas['StaffOldSiteBookingRequestInput'] = {
      // The period holds no promo code for a booking from the old site.
      ...period,
      ...(vehicleId ? { vehicleId } : {}),
      customer: who,
      ...licenceBody(),
      locale: customerLocale,
      ...(ferry ? { ferry } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      oldSite: carried.request,
    }
    // One key per request, as for any booking: a retry replays, a corrected one is new.
    const serialized = JSON.stringify(body)
    if (lastCarried.current?.body !== serialized) {
      lastCarried.current = { body: serialized, key: newIdempotencyKey() }
    }
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/old-site', {
        body,
        params: { header: { 'idempotency-key': lastCarried.current.key } },
      }),
    )
    setBusy(false)
    carryDialog.current?.close()
    if (result.ok) {
      leaving.current = true
      void navigate({
        to: '/bookings/$code',
        params: { code: result.data.code },
        search: { created: true },
      })
      return
    }
    if (takeOver(result.problem)) return
    setProblem(result.problem)
  }

  const refusals =
    problem?.kind === 'api' && problem.code === 'quote_refused'
      ? ((problem.details.refusals as ApiSchemas['Refusal'][] | undefined) ?? [])
      : []
  // S9: a reference or a Stripe payment carried over already names its booking: a way to it.
  const takenBy =
    problem?.kind === 'api' &&
    (problem.code === 'old_site_reference_taken' || problem.code === 'stripe_payment_taken') &&
    typeof problem.details.code === 'string'
      ? problem.details.code
      : null
  const waived = carrying ? (current?.waived ?? []) : []
  // S2: what the website alone refuses (stop sale): the counter books it, and is told.
  const counterOnly = carrying ? [] : (current?.waived ?? [])
  // S9: a booking from the old site costs what was agreed there, whatever the list says.
  const total = carrying
    ? carried.agreedCents
    : shown?.quote
      ? (overrideCents ?? shown.quote.totalCents)
      : null
  // Said once a price is in: the rail's table is not read out each time it changes.
  const said = current
    ? [
        ...current.refusals.map((refusal) => refusalText(refusal, locale, m, lookups)),
        ...(waived.length > 0
          ? [
              m.newBooking.oldSite.waivedTitle,
              ...waived.map((refusal) => refusalText(refusal, locale, m, lookups)),
            ]
          : []),
        ...(counterOnly.length > 0
          ? [
              m.newBooking.counterOnlyTitle,
              ...counterOnly.map((refusal) => refusalText(refusal, locale, m, lookups)),
            ]
          : []),
        ...(promoRefused ? [promoRefused] : []),
        ...(noRoom ? [m.newBooking.noRoom] : []),
        ...(current.quote
          ? [
              carrying
                ? `${m.newBooking.oldSite.listPrice} ${formatEuro(current.quote.totalCents, locale)}`
                : `${m.booking.total} ${formatEuro(current.quote.totalCents, locale)}`,
            ]
          : []),
      ].join(' ')
    : ''

  return (
    <>
      <PageHeader
        title={m.newBooking.title}
        actions={
          <Button type="button" variant="outline" onClick={cancel}>
            {m.newBooking.cancel}
          </Button>
        }
      />
      <form onSubmit={submit} onKeyDown={noImplicitSubmit} className={RAIL_FORM} noValidate>
        <div className={RAIL_STEPS}>
          <Section title={m.newBooking.source}>
            <ChoiceChips
              name="source"
              label={m.newBooking.source}
              labelHidden
              options={[
                ...STAFF_BOOKING_SOURCES,
                // S9: only for whom the role lets carry a booking over.
                ...(canCarryOver ? (['old_site'] as const) : []),
              ].map((value) => ({
                value,
                label: m.sources[value],
              }))}
              value={source}
              onChange={setSource}
            />
          </Section>

          {carrying ? (
            <OldSiteSection fields={oldSite} onChange={setOldSite} reading={carried} />
          ) : null}

          <Section title={m.newBooking.when}>
            <LookupProblem
              problem={problemOf(locations.error) ?? problemOf(zones.error)}
              what={m.newBooking.placesFailed}
              busy={locations.isFetching || zones.isFetching}
              retry={async () => {
                const [found, delivered] = await Promise.all([locations.refetch(), zones.refetch()])
                return { isSuccess: found.isSuccess && delivered.isSuccess }
              }}
              target={pickupSelect}
            />
            <div className={FIELD_ROW}>
              <FieldPair>
                <Field className={PAIRED_FIELD} label={m.newBooking.pickupDate} required>
                  {(control) => (
                    <StaffInput
                      {...control}
                      type="date"
                      value={pickupDate}
                      onChange={(event) => {
                        setPickupDate(event.target.value)
                        if (event.target.value >= returnDate)
                          setReturnDate(addDays(event.target.value, 1))
                      }}
                    />
                  )}
                </Field>
                <Field className={PAIRED_FIELD} label={m.newBooking.pickupTime} required>
                  {(control) => (
                    <StaffInput
                      {...control}
                      type="time"
                      step={300}
                      value={pickupTime}
                      onChange={(event) => setPickupTime(event.target.value)}
                    />
                  )}
                </Field>
              </FieldPair>
            </div>
            <div className={FIELD_ROW}>
              <FieldPair>
                <Field className={PAIRED_FIELD} label={m.newBooking.returnDate} required>
                  {(control) => (
                    <StaffInput
                      {...control}
                      type="date"
                      min={pickupDate}
                      value={returnDate}
                      onChange={(event) => setReturnDate(event.target.value)}
                    />
                  )}
                </Field>
                <Field className={PAIRED_FIELD} label={m.newBooking.returnTime} required>
                  {(control) => (
                    <StaffInput
                      {...control}
                      type="time"
                      step={300}
                      value={returnTime}
                      onChange={(event) => setReturnTime(event.target.value)}
                    />
                  )}
                </Field>
              </FieldPair>
            </div>
            <div className={FIELD_ROW}>
              <Field className={FIELD} label={m.newBooking.pickupPlace} required>
                {(control) => (
                  <PlaceSelect
                    control={control}
                    selectRef={pickupSelect}
                    value={pickupPlace}
                    onChange={setPickupPlace}
                    lookups={lookups}
                  />
                )}
              </Field>
              <Field className={FIELD} label={m.newBooking.returnPlace}>
                {(control) => (
                  <PlaceSelect
                    control={control}
                    value={returnPlace || pickupPlace}
                    onChange={setReturnPlace}
                    lookups={lookups}
                  />
                )}
              </Field>
            </div>
            {atPort ? (
              <div className={FIELD_ROW}>
                <Field
                  className={FIELD}
                  label={m.newBooking.ferryName}
                  hint={m.newBooking.ferryNameHint}
                  optionalLabel={m.newBooking.optional}
                >
                  {(control) => (
                    <StaffInput
                      {...control}
                      maxLength={80}
                      autoComplete="off"
                      value={ferryName}
                      onChange={(event) => setFerryName(event.target.value)}
                    />
                  )}
                </Field>
                <Field
                  className={FIELD}
                  label={m.newBooking.ferryTime}
                  hint={m.newBooking.ferryTimeHint}
                  optionalLabel={m.newBooking.optional}
                >
                  {(control) => (
                    <StaffInput
                      {...control}
                      type="time"
                      value={ferryTime}
                      onChange={(event) => setFerryTime(event.target.value)}
                    />
                  )}
                </Field>
              </div>
            ) : null}
          </Section>

          <Section title={m.newBooking.vehicleSection}>
            <LookupProblem
              problem={problemOf(categories.error)}
              what={m.vehicles.categoriesFailed}
              busy={categories.isFetching}
              retry={categories.refetch}
              target={categorySelect}
            />
            <div className={FIELD_ROW}>
              <Field className={FIELD} label={m.newBooking.category} required>
                {(control) => (
                  <StaffSelect
                    {...control}
                    ref={categorySelect}
                    value={categoryId}
                    onChange={(event) => {
                      setCategoryId(event.target.value)
                      setVehicleId('')
                      setExtras({})
                      setCoverageIds([])
                    }}
                  >
                    <option value="">{m.newBooking.chooseCategory}</option>
                    {categories.data?.items.map((category) => (
                      <option key={category.id} value={category.id}>
                        {localized(category.name, locale)}
                      </option>
                    ))}
                  </StaffSelect>
                )}
              </Field>
              <Field
                // "Qualsiasi veicolo libero (da assegnare)" needs 384 px: beside the category only
                // where both fit, never cut short (UI-1c's rule for a list's words).
                className="min-w-0 grow basis-sm"
                label={m.newBooking.vehicle}
                hint={vehiclesWaiting ? m.newBooking.vehiclesWithPrice : undefined}
              >
                {(control) => (
                  <StaffSelect
                    {...control}
                    disabled={vehiclesWaiting}
                    value={vehicleId}
                    onChange={(event) => setVehicleId(event.target.value)}
                  >
                    <option value="">{m.newBooking.anyVehicle}</option>
                    {quote.data?.vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id} disabled={!vehicle.free}>
                        {vehicle.plate} · {vehicle.make} {vehicle.model}
                        {vehicle.free ? '' : ` (${m.newBooking.taken})`}
                      </option>
                    ))}
                  </StaffSelect>
                )}
              </Field>
            </div>
            <LookupProblem
              problem={problemOf(options.error)}
              what={m.newBooking.optionsFailed}
              busy={options.isFetching}
              retry={options.refetch}
              target={optionsArea}
            />
            {/* What the options' Riprova fills, and the code after it: the focus lands on its
                first control (UI-2g). No box of its own: its parts stand in the step's column. */}
            <div ref={optionsArea} className="contents">
              {options.data &&
              (options.data.extras.length > 0 || options.data.coverages.length > 0) ? (
                <div className={FIELD_ROW}>
                  {options.data.extras.length > 0 ? (
                    <fieldset className={cn(FIELD, 'flex flex-col gap-2')}>
                      <legend className="text-sm font-medium">{m.newBooking.extras}</legend>
                      {options.data.extras.map((extra) =>
                        // W2-S S6: equipment every rental takes is shown as the included
                        // cover is, ticked and fixed; the quote adds it whatever is sent.
                        extra.included ? (
                          <Checkbox
                            key={extra.id}
                            label={localized(extra.name, locale)}
                            checked
                            disabled
                            onChange={() => undefined}
                          />
                        ) : (
                          <Field key={extra.id} label={localized(extra.name, locale)}>
                            {(control) => (
                              <StaffInput
                                {...control}
                                type="number"
                                inputMode="numeric"
                                min={0}
                                max={extra.maxPerBooking ?? 20}
                                value={extras[extra.id] ?? 0}
                                onChange={(event) =>
                                  setExtras({
                                    ...extras,
                                    [extra.id]: Math.max(0, Number(event.target.value) || 0),
                                  })
                                }
                              />
                            )}
                          </Field>
                        ),
                      )}
                    </fieldset>
                  ) : null}
                  {options.data.coverages.length > 0 ? (
                    <fieldset className={cn(FIELD, 'flex flex-col gap-1')}>
                      <legend className="text-sm font-medium">{m.newBooking.cover}</legend>
                      {options.data.coverages.map((coverage) => (
                        <Checkbox
                          key={coverage.id}
                          label={localized(coverage.name, locale)}
                          checked={coverage.included || coverageIds.includes(coverage.id)}
                          disabled={coverage.included}
                          onChange={(event) =>
                            setCoverageIds(
                              event.target.checked
                                ? [...coverageIds, coverage.id]
                                : coverageIds.filter((id) => id !== coverage.id),
                            )
                          }
                        />
                      ))}
                    </fieldset>
                  ) : null}
                </div>
              ) : null}
              {/* S9: the price agreed on the old site has whatever it gave: no code. */}
              <div className={cn(FIELD_ROW, carrying && 'hidden')}>
                <Field
                  label={m.newBooking.promoCode}
                  optionalLabel={m.newBooking.optional}
                  error={promoRefused}
                  className={cn(FIELD, 'max-w-xs')}
                >
                  {(control) => (
                    <StaffInput
                      {...control}
                      value={promoInput}
                      autoCapitalize="characters"
                      onChange={(event) => setPromoInput(event.target.value)}
                      // Enter applies the code, as leaving the field does: it never sends the booking.
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter') return
                        event.preventDefault()
                        setPromoCode(promoInput.trim().toUpperCase())
                      }}
                      onBlur={() => setPromoCode(promoInput.trim().toUpperCase())}
                    />
                  )}
                </Field>
              </div>
            </div>
          </Section>

          <Section title={m.newBooking.customerSection}>
            <ChoiceChips
              name="customer-mode"
              label={m.newBooking.customerSection}
              labelHidden
              options={[
                { value: 'new', label: m.newBooking.newCustomer },
                { value: 'existing', label: m.newBooking.existingCustomer },
              ]}
              value={customerMode}
              onChange={setCustomerMode}
            />
            {customerMode === 'existing' ? (
              customerId !== null && preset.isPending && chosen === null ? (
                <Loading />
              ) : (
                <>
                  {/* The customer this booking was started for, if their record did not come:
                      gone (or never one: 404, 400) is said in the counter's words, by the search. */}
                  {choseAnother ? null : presetMissing || presetRefused ? (
                    <Notice tone="warning">
                      <p>{presetRefused ?? m.errors.codes.customer_not_found}</p>
                    </Notice>
                  ) : (
                    <ProblemNotice problem={presetProblem} />
                  )}
                  <CustomerPicker
                    chosen={chosen}
                    onChoose={(next) => {
                      if (next) {
                        presetApplied.current = true
                        setChoseAnother(true)
                      }
                      setChosen(next)
                    }}
                  />
                </>
              )
            ) : (
              <>
                <div className={FIELD_ROW}>
                  <Field className={FIELD} label={m.newBooking.firstName} required>
                    {(control) => (
                      <StaffInput
                        {...control}
                        autoComplete="off"
                        value={customer.firstName}
                        onChange={(event) =>
                          setCustomer({
                            ...customer,
                            firstName: event.target.value,
                          })
                        }
                      />
                    )}
                  </Field>
                  <Field className={FIELD} label={m.newBooking.lastName} required>
                    {(control) => (
                      <StaffInput
                        {...control}
                        autoComplete="off"
                        value={customer.lastName}
                        onChange={(event) =>
                          setCustomer({
                            ...customer,
                            lastName: event.target.value,
                          })
                        }
                      />
                    )}
                  </Field>
                </div>
                <div className={FIELD_ROW}>
                  <Field
                    className={FIELD}
                    label={m.newBooking.phone}
                    hint={m.newBooking.phoneHint}
                    optionalLabel={remote ? undefined : m.newBooking.optional}
                  >
                    {(control) => (
                      <StaffInput
                        {...control}
                        type="tel"
                        autoComplete="off"
                        value={customer.phone}
                        onChange={(event) =>
                          setCustomer({
                            ...customer,
                            phone: event.target.value,
                          })
                        }
                      />
                    )}
                  </Field>
                  <Field
                    className={FIELD}
                    label={m.newBooking.email}
                    optionalLabel={remote ? undefined : m.newBooking.optional}
                  >
                    {(control) => (
                      <StaffInput
                        {...control}
                        type="email"
                        autoComplete="off"
                        value={customer.email}
                        onChange={(event) =>
                          setCustomer({
                            ...customer,
                            email: event.target.value,
                          })
                        }
                      />
                    )}
                  </Field>
                </div>
                {remote ? (
                  <p className="text-sm text-text-muted">
                    {carrying ? m.newBooking.oldSite.contactNeeded : m.newBooking.contactNeeded}
                  </p>
                ) : null}
                <div className={cn(FIELD_ROW, 'items-end')}>
                  <FieldPair>
                    <Field
                      className={PAIRED_FIELD}
                      label={m.newBooking.birthDate}
                      optionalLabel={m.newBooking.optional}
                    >
                      {(control) => (
                        <StaffInput
                          {...control}
                          type="date"
                          value={customer.birthDate}
                          onChange={(event) =>
                            setCustomer({
                              ...customer,
                              birthDate: event.target.value,
                            })
                          }
                        />
                      )}
                    </Field>
                    <Field
                      className={PAIRED_FIELD}
                      label={m.newBooking.nationality}
                      optionalLabel={m.newBooking.optional}
                    >
                      {(control) => (
                        <StaffInput
                          {...control}
                          maxLength={2}
                          autoCapitalize="characters"
                          value={customer.nationality}
                          onChange={(event) =>
                            setCustomer({
                              ...customer,
                              nationality: event.target.value.toUpperCase(),
                            })
                          }
                        />
                      )}
                    </Field>
                  </FieldPair>
                </div>
              </>
            )}
            <div className={FIELD_ROW}>
              <Field
                label={carrying ? m.newBooking.oldSite.language : m.newBooking.language}
                className={cn(FIELD, 'max-w-xs')}
              >
                {(control) => (
                  <StaffSelect
                    {...control}
                    value={customerLocale}
                    onChange={(event) => setCustomerLocale(event.target.value as Locale)}
                  >
                    {LOCALES.map((value) => (
                      <option key={value} value={value}>
                        {m.languages[value]}
                      </option>
                    ))}
                  </StaffSelect>
                )}
              </Field>
            </div>
            {/* One look for what is closed until asked for (UI-2g), the technical details' too. */}
            <Disclosure summary={m.newBooking.licenceSection} surface="well">
              <div className="flex flex-col gap-4">
                <div className={FIELD_ROW}>
                  <Field className={FIELD} label={m.newBooking.licenceNumber}>
                    {(control) => (
                      <StaffInput
                        {...control}
                        autoComplete="off"
                        value={licence.number}
                        onChange={(event) => setLicence({ ...licence, number: event.target.value })}
                      />
                    )}
                  </Field>
                  <Field className={SHORT_FIELD} label={m.newBooking.licenceCountry}>
                    {(control) => (
                      <StaffInput
                        {...control}
                        maxLength={2}
                        value={licence.country}
                        onChange={(event) =>
                          setLicence({
                            ...licence,
                            country: event.target.value.toUpperCase(),
                          })
                        }
                      />
                    )}
                  </Field>
                </div>
                <div className={cn(FIELD_ROW, 'items-end')}>
                  <Field className={SHORT_FIELD} label={m.newBooking.licenceCategory}>
                    {(control) => (
                      <StaffInput
                        {...control}
                        maxLength={10}
                        value={licence.category}
                        onChange={(event) =>
                          setLicence({
                            ...licence,
                            category: event.target.value.toUpperCase(),
                          })
                        }
                      />
                    )}
                  </Field>
                  <FieldPair>
                    <Field className={PAIRED_FIELD} label={m.newBooking.licenceFirstIssued}>
                      {(control) => (
                        <StaffInput
                          {...control}
                          type="date"
                          value={licence.firstIssued}
                          onChange={(event) =>
                            setLicence({
                              ...licence,
                              firstIssued: event.target.value,
                            })
                          }
                        />
                      )}
                    </Field>
                    <Field className={PAIRED_FIELD} label={m.newBooking.licenceExpires}>
                      {(control) => (
                        <StaffInput
                          {...control}
                          type="date"
                          value={licence.expires}
                          onChange={(event) =>
                            setLicence({
                              ...licence,
                              expires: event.target.value,
                            })
                          }
                        />
                      )}
                    </Field>
                  </FieldPair>
                </div>
                <Checkbox
                  label={m.newBooking.hasIdp}
                  checked={licence.hasIdp}
                  onChange={(event) => setLicence({ ...licence, hasIdp: event.target.checked })}
                />
              </div>
            </Disclosure>
          </Section>

          {canOverride && !carrying ? (
            <Section title={m.newBooking.overrideSection}>
              <Checkbox
                label={m.newBooking.override}
                checked={override}
                onChange={(event) => setOverride(event.target.checked)}
              />
              {override ? (
                <div className={FIELD_ROW}>
                  <Field
                    className={FIELD}
                    label={m.newBooking.overrideTotal}
                    required
                    error={
                      overrideTotal.trim() !== '' && overrideCents === null
                        ? m.payment.amountInvalid
                        : null
                    }
                  >
                    {(control) => (
                      <StaffInput
                        {...control}
                        inputMode="decimal"
                        value={overrideTotal}
                        onChange={(event) => setOverrideTotal(event.target.value)}
                      />
                    )}
                  </Field>
                  <Field
                    className={FIELD}
                    label={m.newBooking.overrideReason}
                    hint={m.newBooking.overrideReasonHint}
                    required
                  >
                    {(control) => (
                      <StaffInput
                        {...control}
                        maxLength={500}
                        value={overrideReason}
                        onChange={(event) => setOverrideReason(event.target.value)}
                      />
                    )}
                  </Field>
                </div>
              ) : null}
            </Section>
          ) : null}

          <Section title={m.newBooking.notes}>
            <Field label={m.newBooking.notesLabel} optionalLabel={m.newBooking.optional}>
              {(control) => (
                <StaffTextarea
                  {...control}
                  rows={3}
                  maxLength={500}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              )}
            </Field>
          </Section>
        </div>

        <ActionRail
          title={m.newBooking.priceSection}
          action={
            total === null ? (
              carrying ? (
                m.newBooking.oldSite.submit
              ) : (
                m.newBooking.submit
              )
            ) : (
              <>
                {carrying ? m.newBooking.oldSite.submit : m.newBooking.submit}
                <span className="figures">
                  {' · '}
                  {formatEuro(total, locale)}
                </span>
              </>
            )
          }
          detailsLabel={m.newBooking.priceDetails}
          ready={ready}
          busy={busy || rereading}
          reasons={reasons}
          stoppedBy={stoppedBy}
          problem={refusals.length > 0 || takenBy !== null ? null : problem}
          notice={
            refusals.length > 0 ? (
              <Notice tone="danger" live="assertive" title={m.newBooking.notBookable}>
                <ul className="flex list-disc flex-col gap-1 ps-6">
                  {refusals.map((refusal) => (
                    <li key={refusal.code}>{refusalText(refusal, locale, m, lookups)}</li>
                  ))}
                </ul>
              </Notice>
            ) : takenBy !== null && problem ? (
              <Notice tone="danger" live="assertive">
                <p>{problemText(problem, locale, m)}</p>
                <p>
                  <Link
                    to="/bookings/$code"
                    params={{ code: takenBy }}
                    className="inline-flex min-h-touch items-center font-medium underline"
                  >
                    {format(m.newBooking.oldSite.openTaken, { code: takenBy })}
                  </Link>
                </p>
              </Notice>
            ) : priceChanged ? (
              <Notice tone="warning" live="assertive">
                <p>{m.newBooking.priceChanged}</p>
              </Notice>
            ) : null
          }
        >
          <PricePanel
            quote={shown}
            stale={shown !== null && shown !== current}
            complete={quoteBody !== null}
            problem={problemOf(quote.error)}
            vehicleChosen={vehicleId !== ''}
            lookups={lookups}
            refusalsId={refusalsId}
            noRoomId={noRoomId}
            carried={carrying ? carried : null}
          />
          <p role="status" className="sr-only">
            {said}
          </p>
        </ActionRail>
      </form>
      <ConfirmDialog
        dialogRef={discardDialog}
        title={m.newBooking.discardTitle}
        confirmLabel={m.newBooking.discardConfirm}
        cancelLabel={m.newBooking.discardKeep}
        busy={false}
        onConfirm={discard}
        onClose={() => {
          if (blocker.status === 'blocked') blocker.reset()
        }}
      >
        <p>{m.newBooking.discardBody}</p>
      </ConfirmDialog>
      {carrying ? (
        <ConfirmDialog
          dialogRef={carryDialog}
          // Asked nothing, it holds nothing: no hidden copy of a question not yet asked.
          title={
            carried.request
              ? format(m.newBooking.oldSite.confirmTitle, {
                  reference: carried.request.reference,
                })
              : null
          }
          confirmLabel={m.newBooking.oldSite.confirmLabel}
          cancelLabel={m.newBooking.oldSite.correct}
          busy={busy}
          onConfirm={() => void carryOver()}
        >
          {carried.request && carried.balanceCents !== null ? (
            <OldSiteSummary
              request={carried.request}
              balanceCents={carried.balanceCents}
              listCents={current?.quote?.totalCents ?? null}
              waived={waived}
              lookups={lookups}
            />
          ) : null}
        </ConfirmDialog>
      ) : null}
    </>
  )
}

function PlaceSelect({
  control,
  selectRef,
  value,
  onChange,
  lookups,
}: {
  control: Parameters<Parameters<typeof Field>[0]['children']>[0]
  selectRef?: Ref<HTMLSelectElement>
  value: string
  onChange: (value: string) => void
  lookups: Lookups
}) {
  const { locale, messages: m } = useLocale()
  return (
    <StaffSelect
      {...control}
      ref={selectRef}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <optgroup label={m.newBooking.locations}>
        {(lookups.locations as ApiSchemas['Location'][]).map((location) => (
          <option key={location.id} value={`location:${location.id}`}>
            {location.name}
          </option>
        ))}
      </optgroup>
      <optgroup label={m.newBooking.zones}>
        {(lookups.zones as ApiSchemas['DeliveryZone'][]).map((zone) => (
          <option key={zone.id} value={`zone:${zone.id}`}>
            {localized(zone.name, locale)}
          </option>
        ))}
      </optgroup>
    </StaffSelect>
  )
}

/**
 * The rail's price: what each part costs and the total, the server's. While the
 * next price is on its way the last one stays, marked as such, so the rail does
 * not jump; the button waits for the new one. S9: for a booking from the old
 * site, the list's total is only for comparison: the price agreed there, what
 * was paid there and what is left follow it, and the old site's rules it
 * breaks are said as warnings above.
 */
function PricePanel({
  quote,
  stale,
  complete,
  problem,
  vehicleChosen,
  lookups,
  refusalsId,
  noRoomId,
  carried,
}: {
  quote: Quote | null
  stale: boolean
  complete: boolean
  problem: StaffProblem | null
  vehicleChosen: boolean
  lookups: Lookups
  refusalsId: string
  noRoomId: string
  carried: OldSiteReading | null
}) {
  const { locale, messages: m } = useLocale()
  if (!complete) return <p className="text-text-muted">{m.newBooking.incomplete}</p>
  if (!quote) {
    return problem ? (
      <ProblemNotice problem={problem} />
    ) : (
      <Fetching>{m.newBooking.quoting}</Fetching>
    )
  }
  return (
    <div className="flex flex-col gap-3" aria-busy={stale || undefined}>
      {stale ? <Fetching>{m.newBooking.quoting}</Fetching> : null}
      {quote.refusals.length > 0 ? (
        <Notice tone="danger" title={m.newBooking.notBookable}>
          <ul id={refusalsId} className="flex list-disc flex-col gap-1 ps-6">
            {quote.refusals.map((refusal) => (
              <li key={refusal.code}>{refusalText(refusal, locale, m, lookups)}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {!quote.available && !vehicleChosen ? (
        <Notice tone="warning">
          <p id={noRoomId}>{m.newBooking.noRoom}</p>
        </Notice>
      ) : null}
      {carried ? (
        <WaivedRules waived={quote.waived} lookups={lookups} />
      ) : (
        <WaivedRules
          waived={quote.waived}
          lookups={lookups}
          title={m.newBooking.counterOnlyTitle}
        />
      )}
      {quote.quote ? (
        <table className={cn('w-full', stale && 'text-text-muted')}>
          <tbody>
            {quote.quote.lines.map((line) => (
              <tr key={`${line.kind}-${line.code}`} className="border-b border-rule">
                <th scope="row" className="py-2 text-start font-regular">
                  {lineLabel(line, locale, m)}
                  {line.days !== null ? (
                    <span className="text-sm text-text-muted">
                      {' · '}
                      {plural(locale, MESSAGES[locale].common.days, line.days)}
                    </span>
                  ) : null}
                </th>
                <td className="figures py-2 ps-3 text-end">
                  {formatEuro(line.totalCents, locale)}
                </td>
              </tr>
            ))}
            <tr className={cn(carried && 'border-b border-rule')}>
              <th scope="row" className={cn('py-2 text-start', carried && 'font-regular')}>
                {carried ? m.newBooking.oldSite.listPrice : m.booking.total}
              </th>
              <td
                className={cn('figures py-2 ps-3 text-end', !carried && 'font-bold')}
                data-testid="quote-total"
              >
                {formatEuro(quote.quote.totalCents, locale)}
              </td>
            </tr>
            {carried ? <CarriedRows carried={carried} /> : null}
          </tbody>
        </table>
      ) : null}
    </div>
  )
}

/** S9: under the list's total, what was agreed on the old site, paid there, and left to pay. */
function CarriedRows({ carried }: { carried: OldSiteReading }) {
  const { locale, messages: m } = useLocale()
  const words = m.newBooking.oldSite
  return (
    <>
      <tr className="border-b border-rule">
        <th scope="row" className="py-2 text-start">
          {words.agreed}
        </th>
        <td className="figures py-2 ps-3 text-end font-bold" data-testid="agreed-total">
          {carried.agreedCents === null ? '—' : formatEuro(carried.agreedCents, locale)}
        </td>
      </tr>
      {carried.paidCents > 0 ? (
        <tr className="border-b border-rule">
          <th scope="row" className="py-2 text-start font-regular">
            {words.paidThere}
          </th>
          <td className="figures py-2 ps-3 text-end">{formatEuro(-carried.paidCents, locale)}</td>
        </tr>
      ) : null}
      {carried.balanceCents !== null ? (
        <tr>
          <th scope="row" className="py-2 text-start">
            {carried.balanceCents < 0 ? words.credit : words.due}
          </th>
          <td className="figures py-2 ps-3 text-end font-bold">
            {formatEuro(Math.abs(carried.balanceCents), locale)}
          </td>
        </tr>
      ) : null}
    </>
  )
}
